import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/context/DataContext";
import { useEntitlements } from "@/hooks/useEntitlements";
import { ProviderCard } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { Loading } from "@/pages/_shared";
import { CheckCircle2, Circle, ShieldCheck, Building2 } from "lucide-react";

const STAGES = ["New business", "Foundation ready", "Partial visibility", "Full visibility"];

export default function EstablishCredit() {
  const { snapshot } = useData();
  const { ent, reload } = useEntitlements();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  if (!snapshot || !ent) return <Loading />;

  const c = snapshot.company || {};
  const isVerified = (v) => String(v).toUpperCase() === "VERIFIED";
  const checklist = [
    { label: "Register your business entity", done: !!c.legal_name, detail: "Legal name & formation state on file" },
    { label: "Verify your EIN", done: isVerified(c.ein_status), detail: "Employer Identification Number confirmed" },
    { label: "Add a business phone number", done: !!c.phone, detail: "A dedicated line lenders can verify" },
    { label: "Get a D-U-N-S number", done: !!c.duns, detail: "Required for your Dun & Bradstreet file" },
  ];
  const doneCount = checklist.filter((x) => x.done).length;
  const foundationReady = doneCount === checklist.length;

  const bureaus = Object.values(ent.features).filter((f) => f.category === "Business Credit");
  const connectedBureaus = bureaus.filter((f) => f.state === "connected").length;
  const stageIdx = connectedBureaus >= 2 ? 3 : connectedBureaus > 0 ? 2 : foundationReady ? 1 : 0;

  const onUpgrade = (f) => setModal({ feature: f.key, requiredPlan: f.required_plan, returnRoute: "/establish-credit" });
  const onConnect = () => navigate("/business-credit");

  return (
    <div className="mx-auto max-w-4xl animate-fade-up pb-10" data-testid="establish-credit-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Establish business <span className="text-foil">credit</span></h1>
      <p className="mt-1 max-w-2xl text-[14px] text-slate-500">Get the bureaus to recognize your business so you can build a real, fundable credit file. Every status below reflects your actual data — nothing is estimated.</p>

      {/* Foundation checklist — driven by real profile data */}
      <div className="card-spotlight mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="foundation-checklist">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-head text-[15px] font-bold text-slate-900"><Building2 size={16} className="text-[#1a5fd0]" /> Foundation checklist</div>
          <span className="font-mono text-[11px] font-bold text-[#1a5fd0]" data-testid="foundation-progress">{doneCount}/{checklist.length}</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#1a5fd0] transition-all" style={{ width: `${(doneCount / checklist.length) * 100}%` }} />
        </div>
        <div className="mt-4 space-y-1.5">
          {checklist.map((x, i) => (
            <div key={x.label} className="flex animate-fade-up items-start gap-2.5" style={{ animationDelay: `${i * 60}ms` }} data-testid="establish-step">
              {x.done ? <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" /> : <Circle size={17} className="mt-0.5 shrink-0 text-slate-300" />}
              <div>
                <div className={`text-[13.5px] font-semibold ${x.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{x.label}</div>
                <div className="text-[12px] text-slate-400">{x.detail}</div>
              </div>
            </div>
          ))}
        </div>
        {!foundationReady && (
          <button onClick={() => navigate("/business-profile")} data-testid="complete-foundation-btn"
            className="btn-shimmer mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1a5fd0] px-4 py-2 font-head text-[13px] font-bold text-white transition-transform hover:-translate-y-0.5">
            Complete your business profile
          </button>
        )}
      </div>

      {/* Bureau visibility — entitlement-driven honest tri-state */}
      <h2 className="mt-8 font-head text-lg font-extrabold text-slate-900">Bureau visibility</h2>
      <p className="text-[13px] text-slate-500">Connect the major business bureaus to make your file visible to lenders and vendors.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {bureaus.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-[13px] text-slate-500">No bureau connections are configured for your account yet.</div>
        ) : bureaus.map((f, i) => (
          <div key={f.key} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <ProviderCard feature={f} onUpgrade={onUpgrade} onConnect={onConnect} onWaitlist={onConnect} onDetails={onConnect} />
          </div>
        ))}
      </div>

      {/* Visibility stage — derived from real state */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="visibility-stage">
        <div className="font-head text-[15px] font-bold text-slate-900">Stage of credit visibility</div>
        <div className="mt-3 space-y-2">
          {STAGES.map((s, i) => (
            <div key={s} className="flex items-center gap-2.5 text-[13px]">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${i <= stageIdx ? "bg-[#1a5fd0] text-white" : "bg-slate-100 text-slate-400"}`}>{i <= stageIdx ? "✓" : i + 1}</span>
              <span className={i <= stageIdx ? "font-medium text-slate-800" : "text-slate-400"}>{s}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[12px] text-slate-400">Your stage advances automatically as you complete your foundation and connect bureaus — based on real data only.</p>
      </div>

      <p className="mt-8 flex items-center justify-center gap-2 text-center text-[12px] text-slate-400"><ShieldCheck size={14} /> Statuses reflect your actual profile and connected sources. Funding is subject to lender eligibility and approval.</p>

      {modal && <UpgradeModal open onClose={() => { setModal(null); reload(); }} feature={modal.feature} requiredPlan={modal.requiredPlan} returnRoute={modal.returnRoute} ent={ent} />}
    </div>
  );
}
